import React, { HTMLAttributes, useCallback, useEffect, useRef, useState } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  AutocompleteRenderGetTagProps,
  Checkbox,
  Chip,
  ClickAwayListener,
  Drawer,
  IconButton,
  ModalProps,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

import useEvent from "@/hooks/useEvent";
import usePlatform from "@/hooks/usePlatform";
import { convertString } from "@/utils/index";

interface Option {
  code: string;
  name: string;
  type?: string;
}

interface BaseAutocompleteProps {
  placeholder?: string;
  Icon?: React.ReactNode;
  options: Option[];
  selectData: Option[];
  maxCount?: number;
  limitTags?: number;
  loading?: boolean;
  freeSolo?: boolean;
  rowKey?: (data: Option) => string;
  renderItem?: (data: Option) => React.ReactNode;
  onChange: (data: Option[]) => void;
  onInputChange?: (value: string) => void;
  ModalProps?: ModalProps;
}

const BaseAutocomplete = ({
  Icon,
  placeholder,
  maxCount = 3,
  limitTags = 3,
  selectData,
  options,
  loading,
  freeSolo,
  rowKey,
  renderItem,
  onChange,
  onInputChange,
  ModalProps,
}: BaseAutocompleteProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { isMobile } = usePlatform();
  const [open, setOpen] = useState(false);
  const [openDrawer, setDrawer] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const handleOpenClick = useEvent(() => {
    if (isMobile) {
      setDrawer((pre) => !pre);
    } else {
      setOpen((pre) => !pre);
    }
  });

  const handleChange = useEvent((e: React.SyntheticEvent, data: Array<Option | string>) => {
    if (selectData.length < maxCount) {
      onChange(data as Option[]);
    } else {
      if (selectData.length > data.length) {
        onChange(data as Option[]);
      }
    }
  });

  const handleInputChange = useEvent((value: string) => {
    onInputChange && onInputChange(value);
  });

  const getKey = useEvent((data: Option) => {
    if (rowKey) {
      return rowKey(data);
    } else {
      return data.code;
    }
  });

  useEffect(() => {
    if (!open) {
      setInputValue("");
      handleInputChange("");
    }
  }, [open, handleInputChange]);

  useEffect(() => {
    if (!openDrawer) {
      setInputValue("");
      handleInputChange("");
    }
  }, [openDrawer, handleInputChange]);

  const renderOption = useCallback(
    (props: HTMLAttributes<HTMLElement>, option: Option) => {
      const selected = selectData.findIndex((ele) => getKey(ele) === getKey(option)) !== -1;

      return (
        <li
          {...props}
          style={{ padding: "4px", paddingLeft: "6px" }}
          key={getKey(option)}
          onClick={(e) => {
            e.stopPropagation();

            if (selected) {
              onChange(selectData.filter((ele) => getKey(ele) !== getKey(option)));
            } else {
              if (selectData.length < maxCount) {
                onChange([...selectData, option]);

                inputRef.current?.blur();

                setTimeout(() => {
                  setOpen(false);
                  setDrawer(false);
                }, 0);
              }
            }
          }}
        >
          <Checkbox
            icon={<CheckBoxOutlineBlankIcon />}
            checkedIcon={<CheckBoxIcon />}
            checked={selected}
          />

          {renderItem ? (
            renderItem(option)
          ) : (
            <div className="pl-[4px]">
              <div className="flex text-sm">
                {convertString(option.name).trim()}
                {`  (${convertString(option.code).trim()})`}
              </div>
            </div>
          )}
        </li>
      );
    },
    [selectData, getKey, onChange, renderItem, maxCount]
  );

  const renderMaximumTip = useCallback(() => {
    return selectData.length >= maxCount ? (
      <div>
        <Typography className="py-[5px] pl-[12px] " variant="body2" style={{ color: "#f44336" }}>
          Maximum number of locations selected
        </Typography>
      </div>
    ) : null;
  }, [selectData, maxCount]);

  const renderTags = useCallback(
    (value: Option[], getTagProps: AutocompleteRenderGetTagProps) =>
      value.map((option, index) => (
        <Chip
          label={option.code}
          {...getTagProps({ index })}
          key={option.code}
          sx={{
            "& .MuiChip-label": {
              paddingRight: "8px",
            },
          }}
          deleteIcon={
            <IconButton size="small" sx={{ width: "21px", height: "21px" }}>
              <CloseIcon className="!text-[16px]" />
            </IconButton>
          }
        />
      )),
    []
  );

  const renderPcAutocomplete = () => {
    return (
      <Autocomplete
        multiple
        clearOnBlur
        onOpen={() => {
          setOpen(true);
        }}
        disableCloseOnSelect
        options={options}
        value={selectData}
        limitTags={limitTags}
        onChange={handleChange}
        loading={loading}
        freeSolo={freeSolo}
        filterOptions={() => options}
        sx={{
          "& .MuiInputBase-input": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: "1px !important",
            minWidth: "1px!important",
            paddingRight: "0 !important",
          },
        }}
        PaperComponent={(props: HTMLAttributes<HTMLElement>) => (
          <Paper
            {...props}
            elevation={8}
            style={{
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
            }}
          >
            {renderMaximumTip()}
            {props.children}
          </Paper>
        )}
        getOptionLabel={(option: Option | string) =>
          typeof option === "string" ? option : convertString(option.code)
        }
        renderOption={renderOption}
        renderTags={renderTags}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            inputRef={inputRef}
            value={inputValue}
            placeholder={selectData.length ? "" : placeholder}
            onChange={(e) => {
              const trimValue = e.target.value.trim();
              setInputValue(trimValue);
              handleInputChange(trimValue);
            }}
            InputProps={{
              style: { paddingLeft: "4px" },
              ...params.InputProps,
              startAdornment: (
                <>
                  <IconButton size="small">{Icon}</IconButton>
                  {params.InputProps.startAdornment ? params.InputProps.startAdornment : null}
                </>
              ),
            }}
          />
        )}
      />
    );
  };

  const renderMobileAutocomplete = () => {
    return isMobile ? (
      <Autocomplete
        multiple
        disablePortal
        openOnFocus
        popupIcon={null}
        clearOnBlur={false}
        disableCloseOnSelect
        options={options}
        value={selectData}
        limitTags={limitTags}
        loading={loading}
        freeSolo={freeSolo}
        onChange={handleChange}
        filterOptions={() => options}
        PaperComponent={(props: HTMLAttributes<HTMLElement>) => (
          <Paper {...props} elevation={1} style={{ boxShadow: "none", height: "100%" }}>
            {renderMaximumTip()}
            {props.children}
          </Paper>
        )}
        ListboxProps={{ style: { maxHeight: "100vh" } }}
        sx={{
          "& .MuiInputBase-root": {
            padding: "10px 0 10px 48px",
            background: "transparent",
          },
          "& :before": {
            opacity: 0.4,
          },
        }}
        getOptionLabel={(option: Option | string) =>
          typeof option === "string" ? option : convertString(option.code)
        }
        renderOption={renderOption}
        renderTags={renderTags}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus
            inputRef={inputRef}
            variant="filled"
            placeholder={selectData.length ? "" : placeholder}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              handleInputChange(e.target.value);
            }}
          />
        )}
      />
    ) : null;
  };

  return (
    <>
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        {isMobile ? (
          <div className="w-full" onClick={handleOpenClick}>
            <Autocomplete
              fullWidth
              multiple
              disablePortal
              disableCloseOnSelect
              clearOnBlur={false}
              limitTags={3}
              options={[]}
              value={selectData}
              popupIcon={null}
              sx={{
                "& .MuiInputBase-root": {
                  paddingRight: "0px !important",
                },
                "& .MuiInputBase-input": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  visibility: "hidden",
                  width: "1px",
                  minWidth: "1px !important",
                },
                "& .MuiAutocomplete-popper": {
                  display: "none",
                },
              }}
              freeSolo={freeSolo}
              PaperComponent={() => <div className="hidden" />}
              onChange={handleChange}
              renderTags={renderTags}
              renderInput={(params) => (
                <TextField
                  {...params}
                  InputProps={{
                    style: { paddingLeft: "4px" },
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <IconButton size="small">{Icon}</IconButton>
                        {params.InputProps.startAdornment ? (
                          params.InputProps.startAdornment
                        ) : (
                          <Typography color="#909092">{placeholder}</Typography>
                        )}
                      </>
                    ),
                  }}
                />
              )}
              getOptionLabel={(option: Option) => convertString(option.code)}
              renderOption={() => null}
            />
          </div>
        ) : (
          renderPcAutocomplete()
        )}
      </ClickAwayListener>

      {/* mobile */}
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => setDrawer(false)}
        // className="!z-[1350]"
        PaperProps={{
          elevation: 1,
          style: { width: "100%" },
        }}
        ModalProps={ModalProps}
      >
        <div className="relative h-full overflow-hidden">
          <div className="h-full">{renderMobileAutocomplete()}</div>

          <div className="absolute top-0 left-0 h-[58px] w-[50px] flex items-center justify-center z-[100px]">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setDrawer(false);
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default BaseAutocomplete;
